/**
 * EventDetailsCard.js — Valle della Pace detailed event card.
 *
 * OVERRIDES: src/components/base/EventDetailsCard.js
 *
 * MIGRATION PARITY (old createEventDetails, valledellapace/script.js 3037-3072):
 *   A decorative top banner (the static eventdetail.png — same for every event),
 *   then the date, title, a thin divider, the first description paragraph, a
 *   price row prefixed with a ticket icon, and a reservation button with a
 *   calendar icon. (NOT the goose banner-curve + cover-image layout.)
 *
 * Reference: valledellapace/script.js Lines 3037-3072 (createEventDetails).
 */

const TICKET_ICON =
  '<svg viewBox="0 0 34 34" width="30" height="30" fill="none" aria-hidden="true"><rect width="34" height="33.72" rx="4" fill="#AE6580" fill-opacity="0.15"/><path d="M11.95 21.02v3.93h1.93v0.77H9.26v-0.77h1.92v-3.93c-1.7-0.64-2.85-5.39-3.07-6.39L8 14.16h7.13l-0.11 0.47c-0.22 1-1.37 5.75-3.07 6.39Z" fill="#AE6580"/><path d="M25.61 16.72c-0.2-0.49-0.73-0.91-1.3-1.36-0.42-0.33-0.89-0.7-1.02-0.98-0.52-1.05-0.15-4.65 0.03-5.94L23.38 8h-3.2l0.06 0.44c0.19 1.29 0.55 4.89 0.04 5.94-0.14 0.28-0.61 0.65-1.02 0.98-0.57 0.45-1.11 0.87-1.31 1.36-0.84 2.03-0.05 8.4-0.02 8.67l0.04 0.34h7.62l0.04-0.34c0.04-0.27 0.81-6.64-0.03-8.67Zm-0.35 4h-6.94v-1.54h6.94v1.54Z" fill="#AE6580"/></svg>';

const RESERVE_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8"  y1="2" x2="8"  y2="6"/><line x1="3"  y1="10" x2="21" y2="10"/><polyline points="9 15 11 17 15 13"/></svg>';

const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

export default class EventDetailsCard {
  render(ev) {
    const card = document.createElement("div");
    card.className = "ws-card ws-event-details-card ws-vp-event-details";

    // Decorative top banner (static, same for all events — old .detail-card-top-design).
    const top = document.createElement("div");
    top.className = "ws-vp-ed-top";
    const topImg = document.createElement("img");
    topImg.src = "assets/valledellapace/eventdetail.png";
    topImg.alt = "";
    topImg.loading = "lazy";
    top.appendChild(topImg);
    card.appendChild(top);

    if (ev.date) {
      const d = document.createElement("span");
      d.className = "ws-vp-ed-date";
      d.textContent = ev.date;
      card.appendChild(d);
    }

    if (ev.title) {
      const title = document.createElement("p");
      title.className = "ws-vp-ed-title";
      title.title = ev.title;
      title.textContent = ev.title;
      card.appendChild(title);
    }

    const hr = document.createElement("div");
    hr.className = "ws-vp-ed-divider";
    card.appendChild(hr);

    // First description paragraph only (old uses description[0]).
    const desc = Array.isArray(ev.description) ? ev.description[0] : ev.description;
    if (desc) {
      const p = document.createElement("p");
      p.className = "ws-vp-ed-desc";
      p.textContent = desc;
      card.appendChild(p);
    }

    const priceText = [ev.price, ev.price_unit].filter(Boolean);
    if (priceText.length) {
      const priceRow = document.createElement("div");
      priceRow.className = "ws-vp-ed-price";
      priceRow.innerHTML = `${TICKET_ICON}<p>${esc(ev.price || "")} <span class="ws-vp-ed-amount">${esc(ev.price_unit || "")}</span></p>`;
      card.appendChild(priceRow);
    }

    // Parity: old createEventDetails rendered the reserve button UNCONDITIONALLY
    // (script.js 3058 — the <button> was always emitted with cta_text, and
    // window.open(cta_link) ran on click). So gate on cta_text, not cta_link: the
    // button always shows; cta_link (set in Feature Cards config) is the
    // destination when present. Valle-scoped (this variant component only).
    const reserveText = ev.cta_text || (ev.cta_link ? "Reserve your spot" : "");
    if (reserveText) {
      const cta = document.createElement("a");
      cta.className = "ws-vp-ed-reserve";
      if (ev.cta_link) {
        cta.href = ev.cta_link;
        cta.target = "_blank";
        cta.rel = "noopener noreferrer";
      }
      cta.innerHTML = `${RESERVE_ICON}<span>${esc(reserveText)}</span>`;
      card.appendChild(cta);
    }

    return card;
  }
}
